<template>
	<BlockUI :blocked="blocked">
		<div class="p-fluid p-input-filled p-formgrid p-grid p-pb-2 p-px-2">
			<div class="p-field p-col-4 p-ml-auto">
				<Dropdown
					v-model="selectedLanguage"
					:options="languages"
					optionLabel="name"
					optionValue="code"
					:placeholder="$t('System-Administration.Language.Selection')"
					@change="changeLanguage"
				/>
			</div>
			<p>{{ $t('System-Administration.General.LoginDescription') }}</p>
			<div class="p-field p-col-12">
				<label for="user">{{$t('System-Administration.Login.UserName')}}</label>
				<InputText id="user" v-model.trim="user" required="true" @keydown="onKeyDown" autofocus />
			</div>
			<div class="p-field p-col-12">
				<label for="password">{{ $t('System-Administration.Login.Password') }}</label>
				<InputText id="password" v-model.trim="serverPassword" required="true" @keydown="onKeyDown" />
			</div>
			<div class="p-field p-col-12">
				<label for="tenant">{{ $t('System-Administration.Tenants.Tenant') }}</label>
				<InputText id="tenant" v-model.trim="tenant" required="true" @keydown="onKeyDown" />
			</div>
			<div class="p-col-12 p-mt-2">
				<Button :label="$t('System-Administration.General.Send')" @click="sendAuth"/>
			</div>
		</div>
	</BlockUI>
</template>

<script lang="ts">
import {computed, defineComponent, onBeforeMount, ref, watch} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import InputText from 'primevue/inputtext';
import Button from "primevue/button";
import BlockUI from 'primevue/blockui';
import Dropdown from 'primevue/dropdown';
import {ToastManager} from "@/util/ToastManager";

export default defineComponent({
	name: "SetupForm",
	components: {
		InputText, Button, BlockUI, Dropdown,
	},
	props: {
		clearTenantField: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["sendAuth", "verifyKey"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const { locale } = useI18n({ useScope: 'global' })

		/** Server Password */
		const serverPassword = ref<string>('');

		/** Tenant */
		const tenant = ref<string>('');

		/** User */
		const user = ref<string>('');

		/** Should the ui be blocked? */
		const blocked = ref<boolean>(false);

		/** Currently selected language */
		const selectedLanguage = ref<string|null>(null);

		/** Languages for UI */
		const languages = computed(() => {
			return [
				{ name: t('System-Administration.Language.German'), code: 'de' },
				{ name: t('System-Administration.Language.English'), code: 'en' },
			]
		});

		/** Triggered to check the auth */
		const sendAuth = () => {
			blocked.value = true;

			if (serverPassword.value === '' || tenant.value === '' || user.value === '') {
				ToastManager.showError(toast, t("System-Administration.Login.LoginFailed"), 'Login-Daten fehlerhaft'); // TODO: add translation
			} else {
				emit("sendAuth", user.value, serverPassword.value, tenant.value);
			}

			blocked.value = false;
		}

		/**
		 * Triggered on keypress
		 * @param event
		 */
		const onKeyDown = (event: {key: string}) => {
			if (event.key === "Enter") {
				sendAuth();
			}
		}

		/** Check Language by first load */
		const checkLanguage = () => {
			if (localStorage.getItem('language')) {
				const language = languages.value.find(language => language.code ===  localStorage.getItem('language'));
				if (language) {
					locale.value = language.code;
					selectedLanguage.value = language.code;
				}
			} else {
				// Check browser language if no local storage language
				const language = languages.value.find(language => language.code === navigator.language.substring(0, 2));
				if (language) {
					locale.value = language.code;
					selectedLanguage.value = language.code;
					localStorage.setItem('language', locale.value);
				}
			}
		}

		/** Triggered when a language is selected */
		const changeLanguage = () => {
			const language = languages.value.find(language => language.code === selectedLanguage.value);
			if (language) {
				locale.value = language.code;
				selectedLanguage.value = language.code;
			}
		}

		watch(() => props.clearTenantField, () => {
			// check if the prop is true, so that clear the tenant field
			if (props.clearTenantField) {
				tenant.value = '';
			}
		})

		onBeforeMount(() => {
			// check language
			checkLanguage();
		})

		return {
			t,
			serverPassword,
			tenant,
			user,
			blocked,
			selectedLanguage,
			languages,
			sendAuth,
			onKeyDown,
			changeLanguage,
		};
	},
});

</script>

<style lang="scss" scoped>

</style>
